import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
export default function AddressLookup({ query, onSelect, autoSearch = false, fallbackLabel = "" }) {
  const [results, setResults] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const autoSearchStarted = useRef(false);
  async function search() {
    setBusy(true);
    setError("");
    setResults(null);
    try {
      const response = (
        await api.get("/research/addresses", { params: { q: query } })
      ).data;
      const normalise = (value) => value.toLowerCase().replace(/[^a-z0-9]/g, "");
      const queryKey = normalise(query);
      const unique = Array.from(new Map(response.candidates.map((candidate) => [normalise(candidate.address), candidate])).values());
      unique.sort((left, right) => {
        const leftKey = normalise(left.address);
        const rightKey = normalise(right.address);
        const leftExact = leftKey === queryKey || leftKey.startsWith(queryKey);
        const rightExact = rightKey === queryKey || rightKey.startsWith(queryKey);
        return Number(rightExact) - Number(leftExact) || left.address.localeCompare(right.address);
      });
      const orderedResponse = { ...response, candidates: unique };
      if (unique.length === 1) {
        onSelect(unique[0]);
        setResults(null);
      } else {
        setResults(orderedResponse);
      }
    } catch (e) {
      setError(
        e.response?.status === 422
          ? "Enter a street number, street name and suburb."
          : fallbackLabel
            ? `Council address search is unavailable. Prop AI will use ${fallbackLabel} suburb-level insights.`
            : "Council address search is unavailable. You can continue with suburb-level insights where available.",
      );
    } finally {
      setBusy(false);
    }
  }
  useEffect(() => {
    if (
      autoSearch &&
      !autoSearchStarted.current &&
      query.trim().length >= 5
    ) {
      autoSearchStarted.current = true;
      search();
    }
    // The component is keyed by the address. Run once for a prefilled address.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSearch, query]);
  return (
    <div className="address-lookup">
      <button
        type="button"
        disabled={busy || query.trim().length < 5}
        onClick={search}
      >
        {busy
          ? "Matching this address…"
          : "Find matching council address"}
      </button>
      <p className="source">
        Brisbane City Council addresses are matched automatically when you
        prepare the report. Include the unit number where relevant.
      </p>
      {error && <p role="status">{error}</p>}
      {results && (
        <div aria-live="polite">
          <p>{results.notice}</p>
          {results.candidates.length === 0 ? (
            <p>
              {fallbackLabel
                ? `No exact match found. Prop AI will use ${fallbackLabel} suburb-level insights and keep parcel-specific checks unassessed.`
                : "No exact match found. Continue with the suburb and postcode; Prop AI will use available suburb-level insights."}
            </p>
          ) : (
            <ul className="address-results">
              {results.candidates.map((candidate) => (
                <li key={candidate.id}>
                  <button
                    type="button"
                    onClick={() => {
                      onSelect(candidate);
                      setResults(null);
                    }}
                  >
                    <strong>{candidate.address}</strong>
                    {candidate.address_use_type && <span className="source">Council use: {candidate.address_use_type}</span>}
                  </button>
                </li>
              ))}
            </ul>
          )}
          <p className="source">
            <a href={results.source_url}>{results.attribution}</a>
          </p>
        </div>
      )}
    </div>
  );
}
